import { useState, useEffect, useRef, useCallback } from 'react'

// Custom hook for delay loading content on user interaction
function useDelayLoad() {
  const [isLoaded, setIsLoaded] = useState(false)
  const elementRef = useRef(null)

  // Expose an imperative trigger so React handlers can unlock lazy UI immediately.
  const loadContent = useCallback(() => {
    setIsLoaded(true)
  }, [])

  const handleInteraction = useCallback((e) => {
    // For keydown events, only trigger on Enter or Space
    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') {
      return
    }

    loadContent()
  }, [loadContent])

  useEffect(() => {
    const element = elementRef.current

    if (!element) {
      return
    }

    // Add event listeners with passive option for better performance
    const events = ['click', 'touchstart', 'mouseenter', 'focus', 'keydown']

    events.forEach(eventType => {
      const options = eventType === 'touchstart' ? { passive: true } : false
      element.addEventListener(eventType, handleInteraction, options)
    })

    // Cleanup event listeners
    return () => {
      events.forEach(eventType => {
        element.removeEventListener(eventType, handleInteraction)
      })
    }
  }, [handleInteraction])

  return { isLoaded, elementRef, loadContent }
}

export default useDelayLoad
